import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    ViewChild,
    AfterViewInit,
} from '@angular/core';
import { ButtonSize } from '../lumi-button/lumi-button.interface';

export type TriState = 'off' | 'auto' | 'on';

@Component({
    selector: 'lumi-tristate-button,lumi-three-state-switch',
    template: `
        <div
                #track
                class="lumi-tristate-toggle"
                role="slider"
                aria-orientation="horizontal"
                [attr.aria-label]="title || 'Mode'"
                [class.full-width]="fullWidth"
                [class.state-off]="state === 'off'"
                [class.state-auto]="state === 'auto'"
                [class.state-on]="state === 'on'"
                [attr.aria-disabled]="disabled"
                [attr.aria-valuemin]="0"
                [attr.aria-valuemax]="2"
                [attr.aria-valuenow]="valueNow()"
                [attr.aria-valuetext]="state"
                tabindex="0"
                (keydown)="onKeydown($event)"
        >
            <div class="thumb" [ngStyle]="thumbStyle"></div>

            <div #segOff class="segment" [class.active]="state === 'off'" (click)="handleSelect('off', $event)">
                <span *ngIf="offIcon" class="material-icons seg-icon">{{ offIcon }}</span>
                <span class="seg-label">{{ displayOffLabel }}</span>
            </div>

            <div #segAuto class="segment" [class.active]="state === 'auto'" (click)="handleSelect('auto', $event)">
                <span *ngIf="autoIcon" class="material-icons seg-icon">{{ autoIcon }}</span>
                <span class="seg-label">{{ displayAutoLabel }}</span>
            </div>

            <div #segOn class="segment" [class.active]="state === 'on'" (click)="handleSelect('on', $event)">
                <span *ngIf="onIcon" class="material-icons seg-icon">{{ onIcon }}</span>
                <span class="seg-label">{{ displayOnLabel }}</span>
            </div>
        </div>
    `,
    styles: [`
        .lumi-tristate-toggle {
            position: relative;
            display: inline-flex;
            align-items: center;
            height: 44px;
            border-radius: 22px;
            background: #e5e5ea;
            padding: 4px;
            box-sizing: border-box;
            cursor: pointer;
            outline: none;
            -webkit-user-select: none;
            user-select: none;
            transition: background 180ms ease;
        }

        .lumi-tristate-toggle.full-width {
            display: flex;
            width: 100%;
        }

        .lumi-tristate-toggle:focus-visible {
            box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.18), 0 0 0 3px rgba(90, 160, 255, 0.45);
        }

        .lumi-tristate-toggle[aria-disabled="true"] {
            opacity: 0.4;
            pointer-events: none;
        }

        .lumi-tristate-toggle.state-off {
            background: #e5e5ea;
        }

        .lumi-tristate-toggle.state-auto {
            background: #d6eaff;
        }

        .lumi-tristate-toggle.state-on {
            background: #d4f0d8;
        }

        .thumb {
            position: absolute;
            top: 4px;
            height: calc(100% - 8px);
            border-radius: 18px;
            background: #ffffff;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.22), 0 0.5px 1px rgba(0, 0, 0, 0.10);
            transition: left 220ms cubic-bezier(0.34, 1.3, 0.64, 1),
            width 220ms cubic-bezier(0.34, 1.3, 0.64, 1);
            pointer-events: none;
            z-index: 1;
        }

        @media (prefers-reduced-motion: reduce) {
            .thumb {
                transition: none;
            }
        }

        .segment {
            position: relative;
            z-index: 2;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
            padding: 0 16px;
            height: 100%;
            border-radius: 18px;
            font-size: 13px;
            font-weight: 500;
            color: rgba(60, 60, 67, 0.5) !important;
            transition: color 160ms ease;
            white-space: nowrap;
        }

        .full-width .segment {
            flex: 1 1 0;
        }

        .segment:hover {
            color: rgba(60, 60, 67, 0.7);
        }

        .segment.active {
            color: rgba(60, 60, 67, 0.9) !important;
        }

        .seg-icon {
            font-size: 15px;
            line-height: 1;
        }

        /* Animate label text changes */
        .seg-label {
            transition: opacity 120ms ease;
        }

        .lumi-tristate-toggle.label-transitioning .seg-label {
            opacity: 0;
        }

        .lumi-tristate-toggle.state-off {
            background: #90ee90;
        }

        .lumi-tristate-toggle.state-auto {
            background: #f2f2f2;
        }

        .lumi-tristate-toggle.state-on {
            background: #90ee90;
        }

        .thumb {
            background: #cccccc;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5), 0 0.5px 1px rgba(0, 0, 0, 0.3);
        }

        .segment {
            color: rgba(235, 235, 245, 0.35);
        }

        .segment:hover {
            color: rgba(235, 235, 245, 0.6);
        }

        .segment.active {
            color: rgba(235, 235, 245, 0.9);
        }
        
    `],
})
export class LumiTristateButtonComponent implements AfterViewInit, OnChanges, OnDestroy {

    @Input() state: TriState = 'auto';

    @Input() size: 'small' | 'medium' | 'large' | ButtonSize = ButtonSize.MEDIUM;
    @Input() disabled = false;
    @Input() fullWidth = false;

    @Input() offLabel = 'Off';
    @Input() autoLabel = 'Auto';
    @Input() onLabel = 'On';

    /**
     * Shorter label versions shown when the toggle is too narrow for the full labels.
     * If not supplied, compactSuffix is stripped automatically from each full label.
     */
    @Input() offShortLabel?: string;
    @Input() autoShortLabel?: string;
    @Input() onShortLabel?: string;

    /**
     * Word(s) to strip from every label when the toggle is too narrow.
     * E.g. compactSuffix="Override" turns "0% Override" → "0%" and "100% Override" → "100%".
     * Has no effect when the explicit *ShortLabel inputs are provided.
     */
    @Input() compactSuffix = '';

    @Input() offIcon?: string;
    @Input() autoIcon?: string;
    @Input() onIcon?: string;

    @Input() title?: string;

    @Output() stateChange = new EventEmitter<TriState>();
    @Output() onClick = new EventEmitter<MouseEvent>();

    @ViewChild('track') trackRef!: ElementRef<HTMLElement>;
    @ViewChild('segOff') segOffRef!: ElementRef<HTMLElement>;
    @ViewChild('segAuto') segAutoRef!: ElementRef<HTMLElement>;
    @ViewChild('segOn') segOnRef!: ElementRef<HTMLElement>;

    thumbStyle: Record<string, string> = {};
    compact = false;

    get displayOffLabel(): string  { return this.compact ? (this.offShortLabel  ?? this.stripSuffix(this.offLabel))  : this.offLabel; }
    get displayAutoLabel(): string { return this.compact ? (this.autoShortLabel ?? this.stripSuffix(this.autoLabel)) : this.autoLabel; }
    get displayOnLabel(): string   { return this.compact ? (this.onShortLabel   ?? this.stripSuffix(this.onLabel))   : this.onLabel; }

    private stripSuffix(label: string): string {
        if (!this.compactSuffix) return label;
        return label.replace(new RegExp('\\s*' + this.compactSuffix + '\\s*', 'gi'), '').trim();
    }

    private resizeObserver?: ResizeObserver;
    /** Natural width (px) of the toggle at full labels — captured on first render. */
    private fullLabelsWidth = 0;

    ngAfterViewInit(): void {
        this.updateThumb(false);

        // Capture the intrinsic width once the full labels are rendered.
        // We add a small buffer (20px) so the switch happens slightly before clipping.
        this.fullLabelsWidth = this.trackRef.nativeElement.scrollWidth + 20;

        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width;
            const shouldBeCompact = this.fullLabelsWidth > 0 && width < this.fullLabelsWidth;

            if (shouldBeCompact !== this.compact) {
                // Fade labels out, swap text, fade back in
                const el = this.trackRef.nativeElement;
                el.classList.add('label-transitioning');
                setTimeout(() => {
                    this.compact = shouldBeCompact;
                    el.classList.remove('label-transitioning');
                    requestAnimationFrame(() => this.updateThumb(false));
                }, 120);
            } else {
                requestAnimationFrame(() => this.updateThumb(false));
            }
        });

        this.resizeObserver.observe(this.trackRef.nativeElement);
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    ngOnChanges(): void {
        setTimeout(() => this.updateThumb(false));
    }

    public handleSelect(next: TriState, $event: MouseEvent): void {
        if (this.disabled) return;
        this.onClick.emit($event);
        this.updateState(next);
    }

    public onKeydown(event: KeyboardEvent): void {
        if (this.disabled) return;
        const key = event.key;
        if (key === 'ArrowLeft' || key === 'Left') {
            event.preventDefault(); this.selectPrev();
        } else if (key === 'ArrowRight' || key === 'Right') {
            event.preventDefault(); this.selectNext();
        } else if (key === 'Home') {
            event.preventDefault(); this.updateState('off');
        } else if (key === 'End') {
            event.preventDefault(); this.updateState('on');
        } else if (key === ' ' || key === 'Spacebar' || key === 'Enter') {
            event.preventDefault(); this.selectNext();
        }
    }

    public valueNow(): number {
        return this.indexOfState(this.state);
    }

    private updateThumb(animate: boolean): void {
        const segEl = this.segForState(this.state)?.nativeElement;
        if (!segEl) return;
        this.thumbStyle = {
            left: `${segEl.offsetLeft}px`,
            width: `${segEl.offsetWidth}px`,
            ...(animate ? {} : { transition: 'none' }),
        };
    }

    private segForState(s: TriState): ElementRef<HTMLElement> | undefined {
        return { off: this.segOffRef, auto: this.segAutoRef, on: this.segOnRef }[s];
    }

    private selectPrev(): void {
        const order: TriState[] = ['off', 'auto', 'on'];
        this.updateState(order[Math.max(0, this.indexOfState(this.state) - 1)]);
    }

    private selectNext(): void {
        const order: TriState[] = ['off', 'auto', 'on'];
        this.updateState(order[Math.min(2, this.indexOfState(this.state) + 1)]);
    }

    private indexOfState(s: TriState): number {
        return { off: 0, auto: 1, on: 2 }[s] ?? 1;
    }

    private updateState(next: TriState): void {
        if (this.state !== next) {
            this.state = next;
            this.stateChange.emit(this.state);
            this.updateThumb(true);
        }
    }
}
